/**
 * Normalizes locale strings to match the actual translation file names.
 * Handles case-insensitive locale matching.
 * 
 * Available locales:
 * - en-US
 * - es-US
 * - vi-US
 * - zh-US-HANS
 */

const AVAILABLE_LOCALES = [
  'en-US',
  'es-US',
  'vi-US',
  'zh-US-HANS'
] as const;

const DEFAULT_LOCALE = 'en-US';

/**
 * Normalizes a locale string to match available translation files (case-insensitive).
 * 
 * @param locale - The locale string from URL or other source (e.g., "zh-us-hans", "ZH-US-HANS", "en-us")
 * @returns The normalized locale string matching the translation file name, or default locale if no match
 * 
 * @example
 * normalizeLocale('zh-US-Hans') // returns 'zh-US-HANS'
 * normalizeLocale('EN-us')      // returns 'en-US'
 * normalizeLocale('invalid')    // returns 'en-US'
 */
export function normalizeLocale(locale: string | null | undefined): string {
  if (!locale) {
    return DEFAULT_LOCALE;
  }

  const upperLocale = locale.toUpperCase();
  
  // Find a case-insensitive match in available locales
  const matchedLocale = AVAILABLE_LOCALES.find(
    availableLocale => availableLocale.toUpperCase() === upperLocale
  );

  return matchedLocale || DEFAULT_LOCALE;
}

/**
 * Gets the normalized locale from URL search params.
 * 
 * @param searchParams - URLSearchParams or query string
 * @returns The normalized locale string
 * 
 * @example
 * getLocaleFromUrl('?locale=zh-US-Hans') // returns 'zh-US-HANS'
 */
export function getLocaleFromUrl(searchParams?: URLSearchParams | string): string {
  const params = typeof searchParams === 'string' 
    ? new URLSearchParams(searchParams)
    : searchParams || new URLSearchParams(window.location.search);
  
  const locale = params.get('locale');
  return normalizeLocale(locale);
}
